<template>
    <Teleport to="body">
        <div
            class="fixed inset-0 z-50 overflow-y-auto"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
        >
            <div
                class="fixed inset-0 bg-gray-600 bg-opacity-50 transition-opacity"
                @click="emit('close')"
            />

            <div class="flex min-h-screen items-center justify-center p-4">
                <div
                    class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto"
                    :class="maxWidthClass"
                    @click.stop
                >
                    <!-- Header -->
                    <div class="flex items-start justify-between p-6">
                        <h3
                            id="modal-title"
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            <slot name="title">{{ title }}</slot>
                        </h3>
                        <button
                            type="button"
                            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                            @click="emit('close')"
                        >
                            <X class="h-6 w-6" />
                        </button>
                    </div>

                    <!-- Body -->
                    <div class="px-6 pb-6">
                        <slot />
                    </div>

                    <!-- Footer -->
                    <div
                        v-if="showFooter"
                        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700"
                    >
                        <slot name="footer" :close="() => emit('close')">
                            <button
                                type="button"
                                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                @click="emit('close')"
                            >
                                {{ cancelText }}
                            </button>
                            <button
                                type="submit"
                                :disabled="loading"
                                class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
                                @click="emit('submit')"
                            >
                                <Loader2
                                    v-if="loading"
                                    class="animate-spin -ml-1 mr-2 h-4 w-4"
                                />
                                {{ submitText }}
                            </button>
                        </slot>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { X, Loader2 } from "@lucide/vue";

interface Props {
    title?: string;
    maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "4xl" | "7xl";
    loading?: boolean;
    submitText?: string;
    cancelText?: string;
    showFooter?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    title: "",
    maxWidth: "md",
    loading: false,
    submitText: "Save",
    cancelText: "Cancel",
    showFooter: true,
});

const emit = defineEmits<{
    close: [];
    submit: [];
}>();

const maxWidthClass = computed(() => {
    const map: Record<string, string> = {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "4xl": "max-w-4xl",
        "7xl": "max-w-7xl",
    };
    return map[props.maxWidth] || "max-w-md";
});
</script>
